import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, AlertTriangle, Wheat, CalendarClock } from "lucide-react";
import { formatDate } from "@/lib/format";

const KIND_ICONS = { low_stock: AlertTriangle, harvest_due: Wheat, reschedule: CalendarClock };

export default function NotificationsBell() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread_count: 0 });

  async function load() {
    try {
      await api.post("/notifications/check");
      const r = await api.get("/notifications");
      setData(r.data);
    } catch { /* ignore */ }
  }
  useEffect(() => {
    if (user) {
      load();
      const iv = setInterval(load, 60000);
      return () => clearInterval(iv);
    }
  }, [user]);

  async function markAll() {
    await api.post("/notifications/read-all");
    load();
  }
  async function markOne(id) {
    await api.post(`/notifications/${id}/read`);
    load();
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 rounded-lg border border-border bg-card flex items-center justify-center hover:border-primary/50 transition-colors" data-testid="notifications-bell">
          <Bell className="w-4 h-4" />
          {data.unread_count > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1" data-testid="notifications-count">
              {data.unread_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-sm">{t("notifications")}</span>
          <Button variant="ghost" size="sm" onClick={markAll} data-testid="notifications-read-all">
            <CheckCheck className="w-4 h-4 mr-1" />{t("mark_all_read")}
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">{t("no_data")}</div>
          ) : data.items.map((n) => {
            const Icon = KIND_ICONS[n.kind] || Bell;
            return (
              <button key={n.id} onClick={() => markOne(n.id)}
                className={`w-full text-left px-4 py-3 border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors flex gap-3 ${n.read ? "opacity-50" : ""}`}
                data-testid={`notification-${n.id}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.kind === "low_stock" ? "bg-[#CC5500]/10 text-[#CC5500]" : "bg-primary/10 text-primary"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{formatDate(n.created_at, lang)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
